import { Controller } from "stimulus";
import { TITLES, statusFor, countStatuses, summaryLabel } from "../daycare_calendar/today";
import { fitPanel, matchesQuery, normalizeQuery, noMatchesMessage, viewportBounds } from "../boarding_calendar/movements";

const MOBILE_QUERY = "(max-width: 639.98px)";
const PANEL_HEIGHT = 560;
const IGNORED_CLICKS = ".popover, .tooltip, .modal, .dropdown-menu, .notyf";
const ROWS = "tr.js-checkin-checkout-row";
const WIDGETS = '[data-toggle="tooltip"], [data-toggle="popover"], [data-toggle="popover-clickable"]';

const onmount = require("onmount");

export default class extends Controller {
  static targets = ["tile", "panel", "title", "tab", "query", "list", "empty", "summary"];
  static values = { url: String };

  connect() {
    this.status = null;
    this.loaded = false;
    this.token = 0;
    this.mediaQuery = window.matchMedia(MOBILE_QUERY);
    this.boundClick = (event) => this.handleDocumentClick(event);
    this.boundKeydown = (event) => this.handleKeydown(event);
    this.boundAttendance = (event) => this.handleAttendance(event);
    this.boundResize = () => this.place();
    document.addEventListener("click", this.boundClick);
    window.addEventListener("resize", this.boundResize);
    document.addEventListener("keydown", this.boundKeydown);
    document.addEventListener("attendanceChanged", this.boundAttendance);
  }

  disconnect() {
    document.removeEventListener("click", this.boundClick);
    document.removeEventListener("keydown", this.boundKeydown);
    document.removeEventListener("attendanceChanged", this.boundAttendance);
    window.removeEventListener("resize", this.boundResize);
    this.lockScroll(false);
    this.disposeWidgets();
  }

  toggle(event) {
    const status = event.currentTarget.dataset.status;
    if (this.status === status) {
      this.close();
      return;
    }

    const opening = this.status === null;
    this.queryTarget.value = "";
    this.setStatus(status);
    if (!opening) return;

    this.panelTarget.hidden = false;
    this.place();
    if (this.mediaQuery.matches) this.lockScroll(true);
    if (event.detail === 0 || this.mediaQuery.matches) this.panelTarget.focus({ preventScroll: true });
    if (!this.loaded) this.load();
    else this.filter();
  }

  selectTab(event) {
    this.setStatus(event.currentTarget.dataset.status);
  }

  close() {
    if (this.status === null) return;

    const tile = this.tileTargets.find((candidate) => candidate.dataset.status === this.status);
    this.status = null;
    this.panelTarget.hidden = true;
    this.lockScroll(false);
    this.syncTiles();
    if (tile && this.element.contains(document.activeElement)) tile.focus();
  }

  search() {
    this.filter();
  }

  searchKeydown(event) {
    if (event.key !== "Escape") return;

    event.stopPropagation();
    if (normalizeQuery(this.queryTarget.value)) {
      this.queryTarget.value = "";
      this.filter();
    } else {
      this.close();
    }
  }

  setStatus(status) {
    this.status = status;
    this.panelTarget.dataset.status = status;
    this.titleTarget.textContent = TITLES[status];
    this.syncTiles();
    this.filter();
  }

  load() {
    const token = ++this.token;
    this.disposeWidgets();
    this.emptyTarget.hidden = true;
    this.listTarget.innerHTML = '<div class="dc-ts-loading"><span class="spinner-border spinner-border-sm text-primary" role="status"><span class="sr-only">Loading</span></span></div>';

    fetch(this.urlValue, { headers: { "X-Requested-With": "XMLHttpRequest" }, credentials: "same-origin" })
      .then((response) => {
        if (!response.ok || response.redirected) throw new Error(`Request failed (${response.status})`);
        return response.text();
      })
      .then((html) => {
        if (token !== this.token) return;
        this.listTarget.innerHTML = html;
        if (this.rows().length === 0) this.listTarget.innerHTML = "";
        this.prepareWidgets();
        this.loaded = true;
        this.recount();
        this.filter();
      })
      .catch(() => {
        if (token !== this.token) return;
        this.listTarget.innerHTML = '<div class="dc-ts-error">This list could not be loaded.</div>';
        if (window.notyf) window.notyf.error("Something went wrong when loading your request. Please try again.");
      });
  }

  rows() {
    return Array.from(this.listTarget.querySelectorAll(ROWS));
  }

  filter() {
    if (!this.loaded || this.status === null) return;

    const query = this.queryTarget.value;
    const rows = this.rows();
    let shown = 0;

    rows.forEach((row) => {
      const match = row.dataset.status === this.status && matchesQuery([row.dataset.petName, row.dataset.ownerName, row.dataset.serviceName], query);
      row.classList.toggle("dc-ts-hide", !match);
      if (match) shown += 1;
    });

    this.emptyTarget.hidden = shown > 0;
    this.emptyTarget.textContent = normalizeQuery(query) ? noMatchesMessage(query) : "No dogs here";
    this.summaryTarget.textContent = summaryLabel(shown, rows.length);
  }

  recount() {
    const counts = countStatuses(this.rows().map((row) => row.dataset.status));
    this.tileTargets.concat(this.tabTargets).forEach((node) => {
      const label = node.querySelector('[data-today="count"]');
      if (label) label.textContent = counts[node.dataset.status];
    });
  }

  handleAttendance(event) {
    const row = this.element.contains(event.target) && event.target.closest(ROWS);
    if (!row) {
      this.loaded = false;
      return;
    }

    const status = statusFor({
      checkedIn: !!row.querySelector(".js-attendance-icon.checked-in:not(.d-none)"),
      checkedOut: !!row.querySelector(".js-attendance-icon.checked-out:not(.d-none)"),
    });
    this.applyStatus(row, status);
  }

  applyStatus(row, status) {
    row.dataset.status = status;
    this.recount();
  }

  syncTiles() {
    this.tileTargets.forEach((tile) => {
      const open = tile.dataset.status === this.status;
      tile.setAttribute("aria-expanded", open ? "true" : "false");
      tile.classList.toggle("dc-ts-tile-open", open);
    });
    this.tabTargets.forEach((tab) => {
      const selected = tab.dataset.status === this.status;
      tab.setAttribute("aria-selected", selected ? "true" : "false");
      tab.tabIndex = selected ? 0 : -1;
    });
  }

  place() {
    const panel = this.panelTarget;
    panel.classList.remove("dc-ts-panel-down");
    panel.style.maxHeight = "";
    if (this.status === null || this.mediaQuery.matches) return;

    const { side, maxHeight } = fitPanel({ anchor: this.element.getBoundingClientRect(), viewport: viewportBounds(), panelHeight: PANEL_HEIGHT });
    panel.style.maxHeight = `${maxHeight}px`;
    if (side === "down") panel.classList.add("dc-ts-panel-down");
  }

  handleDocumentClick(event) {
    if (this.status === null) return;
    const target = event.target;
    if (!target.isConnected) return;
    if (this.element.contains(target) && !target.closest(".dc-cap-btn")) return;
    if (target.closest && target.closest(IGNORED_CLICKS)) return;
    this.close();
  }

  handleKeydown(event) {
    if (event.key === "Escape" && this.status !== null) this.close();
  }

  lockScroll(on) {
    document.body.classList.toggle("dc-ts-modal-open", on);
  }

  prepareWidgets() {
    const $ = window.$;
    const list = this.listTarget;

    if ($) {
      const $list = $(list);
      $list.find('[data-toggle="tooltip"]').tooltip({ sanitize: false, container: "body", boundary: "viewport" });
      $list.find('[data-toggle="popover"]').popover({ sanitize: false, container: "body", boundary: "viewport" });
      $list
        .find('[data-toggle="popover-clickable"]')
        .popover({ sanitize: false, trigger: "manual", container: "body", boundary: "viewport" })
        .on("mouseenter", function () {
          if (!$(this).data("pinned")) $(this).popover("show");
        })
        .on("mouseleave", function () {
          if (!$(this).data("pinned")) $(this).popover("hide");
        })
        .on("click", function (event) {
          event.preventDefault();
          const pinned = $(this).data("pinned");
          $list.find('[data-toggle="popover-clickable"]').data("pinned", false).popover("hide");
          if (!pinned) $(this).data("pinned", true).popover("show");
        });
    }

    onmount();

    list.querySelectorAll(".js-attendance-checkin-toggle").forEach((toggle) => {
      toggle.setAttribute("role", "button");
      toggle.tabIndex = 0;
    });
  }

  disposeWidgets() {
    const $ = window.$;
    if (!$ || !this.hasListTarget) return;

    const $widgets = $(this.listTarget).find(WIDGETS);
    $widgets.tooltip("dispose");
    $widgets.popover("dispose");
  }
}
